import React, { useEffect, useMemo, useState, useRef } from "react";
import { api } from "../lib/api";
import {
  Plus,
  Trash2,
  Copy,
  Link2,
  CalendarClock,
  ExternalLink,
  GripVertical,
  Rows3,
  GanttChartSquare,
  ClipboardCopy,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import RemarksDialog from "./RemarksDialog";
import AgentOwnersBar from "./AgentOwnersBar";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { copyTableToClipboard } from "../lib/copyTable";

const GRID = "28px 150px 160px 150px 120px 150px 150px 170px minmax(180px,1fr) 130px 110px";

// Row-level status (mirrors the Product update sheet): Assigned/WIP/Upgrade/Completed.
const ROW_STATUSES = ["Assigned", "WIP", "Upgrade", "Completed"];
const ROW_STATUS_META = {
  "Assigned":  { bg: "bg-slate-100",  text: "text-slate-600" },
  "WIP":       { bg: "bg-sky-100",    text: "text-sky-700" },
  "Upgrade":   { bg: "bg-butter-100", text: "text-amber-700" },
  "Completed": { bg: "bg-mint-100",   text: "text-emerald-700" },
};

const REF_STATUSES = [
  { value: "Implemented",     bg: "bg-mint-100",   text: "text-emerald-700" },
  { value: "Not great",       bg: "bg-rose-100",   text: "text-rose-700" },
  { value: "To be discussed", bg: "bg-butter-100", text: "text-amber-700" },
  { value: "WIP",             bg: "bg-sky-100",    text: "text-sky-700" },
];
const refStatusMeta = (v) => REF_STATUSES.find((s) => s.value === v);

const STATUS_META = {
  "Live":        { bg: "bg-mint-100",   text: "text-emerald-700" },
  "Testing":     { bg: "bg-lilac-100",  text: "text-purple-700" },
  "Adopting":    { bg: "bg-peach-100",  text: "text-amber-800" },
  "Paused":      { bg: "bg-butter-100", text: "text-amber-700" },
  "WIP":         { bg: "bg-sky-100",    text: "text-sky-700" },
  "Not Started": { bg: "bg-slate-100",  text: "text-slate-600" },
};

// ---------------------------------------------------------------------------
// Inline Date-range + Remark cells (shared by Agent Workflow & Product tables).
// Each row carries a SINGLE primary timeline entry (timeline[0]) whose
// start_date/end_date + task (=remark) drive its bar on the Gantt/Timeline view.
// ---------------------------------------------------------------------------
export const genTimelineId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `tl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

// Strictly a YYYY-MM-DD date with a 4-digit year, real month/day.
const isValidISODate = (v) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v || "")) return false;
  const [y, m, d] = v.split("-").map(Number);
  return m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2999;
};

const fmtShortDate = (iso) => {
  if (!isValidISODate(iso)) return "";   // never render a malformed value
  const [, m, d] = iso.split("-").map(Number);
  return `${d}/${m}`;
};

const rowPrimaryEntry = (row) => (row.timeline && row.timeline[0]) || null;

const buildPrimary = (row, patch) => {
  const prev = rowPrimaryEntry(row) || {
    id: genTimelineId(),
    user: row.workflow_integrator || "",
    task: "",
    start_date: "",
    end_date: "",
    hours: 0,
  };
  const next = { ...prev, ...patch };
  if (next.start_date && !next.end_date) next.end_date = next.start_date;
  if (!next.user && row.workflow_integrator) next.user = row.workflow_integrator;
  return next;
};

export function DateRangeCell({ row, onUpdate, testid }) {
  const [open, setOpen] = useState(false);
  const p = rowPrimaryEntry(row);
  const startProp = p?.start_date || "";
  const endProp = p?.end_date || "";

  // Local draft state so typing into the native date input is never interrupted
  // by the async PATCH + re-render cycle. We only commit on blur / popover close.
  const [start, setStart] = useState(startProp);
  const [end, setEnd] = useState(endProp);

  // Re-sync the draft from props whenever the popover (re)opens.
  useEffect(() => {
    if (open) { setStart(startProp); setEnd(endProp); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const commit = (sRaw, eRaw) => {
    // Only accept well-formed YYYY-MM-DD dates (guards against the native input
    // producing malformed values like a 6-digit year).
    let s = isValidISODate(sRaw) ? sRaw : "";
    let e = isValidISODate(eRaw) ? eRaw : "";
    if (s && !e) e = s;                 // single date → same-day bar
    if (s && e && e < s) { const t = s; s = e; e = t; }  // forgive reversed range
    if (s === startProp && e === endProp) return;        // no change
    onUpdate(row.id, { timeline: [buildPrimary(row, { start_date: s, end_date: e })] });
  };

  const clear = () => {
    setStart(""); setEnd(""); setOpen(false);
    if (startProp || endProp) onUpdate(row.id, { timeline: [buildPrimary(row, { start_date: "", end_date: "" })] });
  };

  const startLbl = fmtShortDate(startProp);
  const endLbl = fmtShortDate(endProp);
  const label = startLbl
    ? (endLbl && endLbl !== startLbl ? `${startLbl} – ${endLbl}` : startLbl)
    : (endLbl || "Set dates");

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) commit(start, end); }}>
      <PopoverTrigger asChild>
        <button
          className={`h-8 w-full px-2 rounded-md border text-xs text-left flex items-center gap-1.5 transition-colors ${
            startProp ? "border-slate-200 text-slate-700 bg-white" : "border-dashed border-slate-300 text-slate-400 hover:border-slate-400"
          }`}
          data-testid={testid}
        >
          <CalendarClock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className="truncate">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-3 space-y-3" align="start">
        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Start date</label>
          <input
            type="date"
            value={start}
            min="2000-01-01"
            max="2999-12-31"
            onChange={(e) => setStart(e.target.value)}
            onBlur={() => commit(start, end)}
            className="w-full h-9 px-2 rounded-md border border-slate-200 text-sm"
            data-testid={`${testid}-start`}
          />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">End date</label>
          <input
            type="date"
            value={end}
            min="2000-01-01"
            max="2999-12-31"
            onChange={(e) => setEnd(e.target.value)}
            onBlur={() => commit(start, end)}
            className="w-full h-9 px-2 rounded-md border border-slate-200 text-sm"
            data-testid={`${testid}-end`}
          />
        </div>
        {(start || startProp) && (
          <button
            onClick={clear}
            className="text-xs text-rose-600 hover:underline"
            data-testid={`${testid}-clear`}
          >
            Clear dates
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function RemarkCell({ row, onUpdate, testid }) {
  const p = rowPrimaryEntry(row);
  const value = p?.task || "";
  const commit = (text) => {
    if (text === value) return;
    onUpdate(row.id, { timeline: [buildPrimary(row, { task: text })] });
  };
  return (
    <input
      key={value}
      defaultValue={value}
      onBlur={(e) => commit(e.target.value.trim())}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      placeholder="Add remark…"
      data-testid={testid}
      className="w-full bg-transparent text-sm text-slate-700 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8 placeholder:text-slate-400"
    />
  );
}

// --- Sortable row ---
function AgentTaskRow({
  r, ag, agentNames, subs, subsubs, models, members, sMeta, refsCount,
  pickAgent, ensureAgentId, handleUpdate, handleDuplicate, handleDelete,
  onOpenRefs,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: r.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    gridTemplateColumns: GRID,
    zIndex: isDragging ? 30 : undefined,
    opacity: isDragging ? 0.9 : 1,
    boxShadow: isDragging ? "0 10px 24px rgba(15, 23, 42, 0.12)" : undefined,
    background: isDragging ? "white" : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      className="grid items-center gap-2 px-4 h-14 border-b border-slate-100 hover:bg-slate-50/60 transition-colors group"
      style={style}
      data-testid={`at-row-${r.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="h-7 w-6 flex items-center justify-center rounded-md text-slate-300 hover:text-slate-600 hover:bg-slate-100 cursor-grab active:cursor-grabbing"
        data-testid={`at-drag-${r.id}`}
        aria-label="Drag to reorder"
        onClick={(e) => e.preventDefault()}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div>
        <Select value={(r.sub_agent ?? ag?.sub_agent) || "__pick__"} onValueChange={(v) => pickAgent(r.id, { sub_agent: v === "—" ? "" : v, sub_sub_agent: undefined, agent_id: "" })} disabled={subs.length === 0}>
          <SelectTrigger className="h-8 text-xs" data-testid={`at-subagent-${r.id}`}><SelectValue placeholder="Pick" /></SelectTrigger>
          <SelectContent>
            {subs.map((n) => <SelectItem key={n || "__empty__"} value={n || "—"}>{n || "—"}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Select value={(r.sub_sub_agent ?? ag?.sub_sub_agent) || "__pick__"} onValueChange={(v) => pickAgent(r.id, { sub_sub_agent: v === "—" ? "" : v, agent_id: "" })} disabled={subsubs.length === 0}>
          <SelectTrigger className="h-8 text-xs" data-testid={`at-subsub-${r.id}`}><SelectValue placeholder="Pick" /></SelectTrigger>
          <SelectContent>
            {subsubs.map((n) => <SelectItem key={n || "__empty__"} value={n || "—"}>{n || "—"}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Select value={ag?.id || "__pick__"} onValueChange={(v) => ensureAgentId(r.id, { agent_name: r.agent_name ?? ag?.agent_name, sub_agent: r.sub_agent ?? ag?.sub_agent, sub_sub_agent: r.sub_sub_agent ?? ag?.sub_sub_agent, model_id: v })} disabled={models.length === 0}>
          <SelectTrigger className="h-8 text-xs" data-testid={`at-model-${r.id}`}><SelectValue placeholder="Pick model" /></SelectTrigger>
          <SelectContent>
            {models.map((m) => <SelectItem key={m.id} value={m.id}>{m.model_name || "—"}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        {ag ? (
          <span className={`inline-flex h-7 px-2 rounded-md items-center text-xs font-medium ${sMeta.bg} ${sMeta.text}`}>{ag.status}</span>
        ) : <span className="text-xs text-slate-400">—</span>}
      </div>
      <div>
        <Select value={r.workflow_integrator || "__none__"} onValueChange={(v) => handleUpdate(r.id, { workflow_integrator: v === "__none__" ? "" : v })}>
          <SelectTrigger className="h-8 text-xs" data-testid={`at-workflow-${r.id}`}><SelectValue placeholder="User" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__"><span className="text-slate-500">Unassigned</span></SelectItem>
            {members.map((m) => <SelectItem key={m.id} value={m.email}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Select value={r.tester_adopter || "__none__"} onValueChange={(v) => handleUpdate(r.id, { tester_adopter: v === "__none__" ? "" : v })}>
          <SelectTrigger className="h-8 text-xs" data-testid={`at-tester-${r.id}`}><SelectValue placeholder="User" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__"><span className="text-slate-500">Unassigned</span></SelectItem>
            {members.map((m) => <SelectItem key={m.id} value={m.email}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <DateRangeCell row={r} onUpdate={handleUpdate} testid={`at-date-${r.id}`} />
      </div>
      <div>
        <RemarkCell row={r} onUpdate={handleUpdate} testid={`at-remark-${r.id}`} />
      </div>
      <div>
        <Select value={r.status || "Assigned"} onValueChange={(v) => handleUpdate(r.id, { status: v })}>
          <SelectTrigger
            className={`h-8 text-xs border-0 font-medium ${(ROW_STATUS_META[r.status] || ROW_STATUS_META["Assigned"]).bg} ${(ROW_STATUS_META[r.status] || ROW_STATUS_META["Assigned"]).text}`}
            data-testid={`at-rowstatus-${r.id}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROW_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-center gap-0.5">
        <button
          onClick={() => onOpenRefs(r)}
          disabled={!ag}
          className={`h-8 w-8 rounded-md flex items-center justify-center relative disabled:opacity-30 ${
            refsCount > 0 ? "text-blue-600 hover:bg-sky-50" : "text-slate-400 hover:text-blue-600 hover:bg-sky-50"
          }`}
          data-testid={`at-refs-${r.id}`}
          title={refsCount > 0 ? `${refsCount} reference${refsCount === 1 ? "" : "s"}` : "References"}
        >
          <Link2 className="h-4 w-4" />
          {refsCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold flex items-center justify-center">
              {refsCount}
            </span>
          )}
        </button>
        <button
          onClick={() => handleDuplicate(r)}
          className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-all"
          data-testid={`at-copy-${r.id}`}
          title="Duplicate"
        >
          <Copy className="h-4 w-4" />
        </button>
        <button
          onClick={() => handleDelete(r.id)}
          className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition-all"
          data-testid={`at-delete-${r.id}`}
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// --- References dialog (shared with Agents, bidirectional) ---
function ReferencesDialog({ open, onOpenChange, agent, onSave }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (open && agent) setItems((agent.references || []).map((r) => ({ ...r })));
  }, [open, agent]);
  const addRow = () => setItems((p) => [...p, { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(-4)}`, url: "", description: "", status: "" }]);
  const updateRow = (id, k, v) => setItems((p) => p.map((it) => (it.id === id ? { ...it, [k]: v } : it)));
  const removeRow = (id) => setItems((p) => p.filter((it) => it.id !== id));
  const submit = async () => {
    try {
      const cleaned = items
        .map((r) => ({ id: r.id, url: (r.url || "").trim(), description: (r.description || "").trim(), status: r.status || "" }))
        .filter((r) => r.url || r.description || r.status);
      await onSave({ references: cleaned, reference_link: cleaned[0]?.url || "" });
      onOpenChange(false);
      toast.success("References saved");
    } catch { toast.error("Failed to save"); }
  };
  if (!agent) return null;
  const COLS = "1.1fr 1.3fr 150px 40px";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl" data-testid="at-references-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">References for {agent.agent_name || agent.code}</DialogTitle>
          <DialogDescription>Changes sync back to the Agents module.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="grid text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 px-3 h-10 items-center bg-slate-100/70 border-b border-slate-200" style={{ gridTemplateColumns: COLS }}>
            <div>Link</div><div>Description</div><div>Status</div><div></div>
          </div>
          {items.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-slate-400">No references yet</div>
          ) : items.map((it) => {
            const sm = refStatusMeta(it.status);
            return (
              <div key={it.id} className="grid items-center gap-2 px-3 h-12 border-b border-slate-100 group" style={{ gridTemplateColumns: COLS }}>
                <div className="flex items-center gap-1">
                  <Input value={it.url} onChange={(e) => updateRow(it.id, "url", e.target.value)} placeholder="https://…" className="h-8 text-sm" />
                  {it.url && (
                    <button type="button" onClick={() => window.open(it.url, "_blank", "noopener,noreferrer")} className="h-7 w-7 rounded-md text-blue-600 hover:bg-sky-50 flex items-center justify-center shrink-0">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <Input value={it.description} onChange={(e) => updateRow(it.id, "description", e.target.value)} placeholder="Short description" className="h-8 text-sm" />
                <Select value={it.status || ""} onValueChange={(v) => updateRow(it.id, "status", v)}>
                  <SelectTrigger className={`h-8 border-none focus:ring-0 focus:ring-offset-0 rounded-md font-medium text-xs px-2 ${sm ? `${sm.bg} ${sm.text}` : "bg-slate-50 text-slate-500"}`}>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {REF_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.value}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={() => removeRow(it.id)} className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
        <div>
          <Button variant="outline" size="sm" onClick={addRow}><Plus className="h-3.5 w-3.5 mr-1" /> Add reference</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="btn-brand">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Timeline (mini-Gantt) dialog ---
export function dateDiffDays(a, b) {
  if (!a || !b) return 0;
  const ms = new Date(b) - new Date(a);
  return Math.max(0, Math.round(ms / 86400000));
}
export function addDays(iso, d) {
  const dt = new Date(iso);
  dt.setDate(dt.getDate() + d);
  return dt.toISOString().slice(0, 10);
}

// --- Flat Timeline view across all rows ---
const DOW_LETTERS = ["S", "M", "T", "W", "T", "F", "S"]; // 0=Sun..6=Sat (will be displayed as M T W T F S S layout via week-start logic)

export function AgentTimelineView({ rows = [], productRows = [], agents = [], members = [], personFilter, onCommit }) {
  const memberByEmail = useMemo(() => {
    const m = new Map();
    for (const x of members) m.set(x.email, x);
    return m;
  }, [members]);
  const agentById = useMemo(() => {
    const m = new Map();
    for (const a of agents) m.set(a.id, a);
    return m;
  }, [agents]);

  const entries = useMemo(() => {
    const out = [];
    // Agent workflow entries
    for (const r of rows) {
      const ag = r.agent_id ? agentById.get(r.agent_id) : null;
      for (const t of (r.timeline || [])) {
        if (!t || (!t.start_date && !t.end_date)) continue;
        out.push({
          source: "agent",
          task_id: r.id,
          row_timeline: r.timeline || [],
          id: t.id,
          person_email: t.user || "",
          person: memberByEmail.get(t.user)?.name || (t.user ? t.user.split("@")[0] : "Unassigned"),
          agent_label: ag ? `${ag.agent_name || "—"} / ${ag.sub_agent || "—"}` : "—",
          model: ag?.model_name || "—",
          task: t.task || "",
          start_date: t.start_date || "",
          end_date: t.end_date || t.start_date || "",
          hours: parseFloat(t.hours) || 0,
        });
      }
    }
    // Product task entries (kind: product | bug)
    for (const r of productRows) {
      const isBug = r.kind === "bug";
      const sourceLabel = isBug ? "Bug" : "Product";
      for (const t of (r.timeline || [])) {
        if (!t || (!t.start_date && !t.end_date)) continue;
        out.push({
          source: "product",
          kind: r.kind || "product",
          task_id: r.id,
          row_timeline: r.timeline || [],
          id: t.id,
          person_email: t.user || "",
          person: memberByEmail.get(t.user)?.name || (t.user ? t.user.split("@")[0] : "Unassigned"),
          agent_label: r.task ? `${sourceLabel} \u00b7 ${r.task}${r.sub_task ? " / " + r.sub_task : ""}` : `${sourceLabel} \u00b7 \u2014`,
          model: "—",
          task: t.task || "",
          start_date: t.start_date || "",
          end_date: t.end_date || t.start_date || "",
          hours: parseFloat(t.hours) || 0,
        });
      }
    }
    const filtered = out.filter((e) => {
      if (personFilter === "__all__" || !personFilter) return true;
      if (personFilter === "__unassigned__") return !e.person_email;
      return e.person_email === personFilter;
    });
    return filtered.sort((a, b) => (a.start_date || "9999").localeCompare(b.start_date || "9999"));
  }, [rows, productRows, agents, members, personFilter, memberByEmail, agentById]);

  // Compute date range for the gantt strip — start on a Monday for clean week alignment
  const { startISO, totalDays } = useMemo(() => {
    let min, max;
    if (entries.length === 0) {
      min = new Date().toISOString().slice(0, 10);
      max = addDays(min, 13);
    } else {
      min = "9999-12-31"; max = "0000-01-01";
      for (const e of entries) {
        if (e.start_date && e.start_date < min) min = e.start_date;
        if (e.end_date && e.end_date > max) max = e.end_date;
        if (e.start_date && e.start_date > max) max = e.start_date;
      }
      if (min === "9999-12-31") min = new Date().toISOString().slice(0, 10);
      if (max === "0000-01-01") max = addDays(min, 13);
    }
    // Snap min back to the previous Monday (so M T W T F S S column header is consistent)
    const minDt = new Date(min + "T00:00:00");
    const dow = minDt.getDay(); // 0=Sun..6=Sat
    const daysSinceMon = dow === 0 ? 6 : dow - 1; // distance from prev Monday
    const snappedMin = addDays(min, -daysSinceMon);
    // Pad to whole weeks and add a buffer so users can drag into the future
    let days = dateDiffDays(snappedMin, max) + 1 + 7; // +1 inclusive, +7 buffer week
    if (days % 7 !== 0) days = days + (7 - (days % 7));
    return { startISO: snappedMin, totalDays: Math.max(21, days) };
  }, [entries]);

  const DAY_W = 36;
  const ganttWidth = totalDays * DAY_W;
  const FIXED = "180px 200px 160px 80px 130px";
  const totalWidth = `calc(${FIXED.split(" ").join(" + ")} + ${ganttWidth}px)`;

  // Day cells for header (every day)
  const dayCells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(startISO + "T00:00:00");
      d.setDate(d.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [startISO, totalDays]);

  const totalHours = entries.reduce((acc, e) => acc + (e.hours || 0), 0);
  const todayISO = new Date().toISOString().slice(0, 10);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="agent-timeline-view">
      <div style={{ minWidth: totalWidth }}>
        {/* Header — two stacked rows for fixed cols (so heights match) and gantt strip */}
        <div className="grid items-stretch bg-slate-100/70 border-b border-slate-200" style={{ gridTemplateColumns: `${FIXED} ${ganttWidth}px` }}>
          <div className="px-3 h-12 flex items-end pb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700">Person</div>
          <div className="px-3 h-12 flex items-end pb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700">Agent / Sub-agent</div>
          <div className="px-3 h-12 flex items-end pb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700">Model</div>
          <div className="px-3 h-12 flex items-end pb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700">Hrs</div>
          <div className="px-3 h-12 flex items-end pb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700">Delivery</div>
          <div className="relative h-12 border-l border-slate-200">
            <div className="absolute inset-0 flex">
              {dayCells.map((d, i) => {
                const dow = d.getDay(); // 0=Sun..6=Sat
                const isMon = dow === 1;
                const isWeekend = dow === 0 || dow === 6;
                const iso = d.toISOString().slice(0, 10);
                const isToday = iso === todayISO;
                return (
                  <div
                    key={i}
                    className={`shrink-0 flex flex-col items-center justify-center select-none ${
                      isMon ? "border-l-2 border-slate-300" : "border-l border-slate-200/60"
                    } ${isWeekend ? "bg-slate-50/80" : ""} ${isToday ? "bg-blue-50/80" : ""}`}
                    style={{ width: DAY_W }}
                    title={iso}
                  >
                    <span className={`text-[10px] font-semibold tracking-wider ${isWeekend ? "text-slate-400" : "text-slate-600"} ${isToday ? "text-blue-700" : ""}`}>
                      {DOW_LETTERS[dow]}
                    </span>
                    <span className={`text-[10px] font-medium leading-tight ${isToday ? "text-blue-700 font-bold" : "text-slate-500"}`}>
                      {d.getDate()}/{d.getMonth() + 1}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Rows */}
        {entries.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-slate-400">
            No timeline entries to show. Set a <strong>Date</strong> range and <strong>Remark</strong> on any row in List view to add it here.
          </div>
        ) : (
          entries.map((e, idx) => (
            <TimelineEditableRow
              key={`${e.task_id}-${e.id}-${idx}`}
              entry={e}
              startISO={startISO}
              totalDays={totalDays}
              DAY_W={DAY_W}
              FIXED={FIXED}
              ganttWidth={ganttWidth}
              onCommit={onCommit}
              dayCells={dayCells}
              todayISO={todayISO}
            />
          ))
        )}

        {/* Footer */}
        {entries.length > 0 && (
          <div className="grid items-center px-3 h-10 bg-slate-50 border-t border-slate-200 text-xs text-slate-700 font-medium" style={{ gridTemplateColumns: `${FIXED} ${ganttWidth}px` }}>
            <div></div>
            <div></div>
            <div className="text-right pr-2">Total</div>
            <div className="font-mono text-emerald-700 font-bold" data-testid="timeline-total-hours">{totalHours}h</div>
            <div></div>
            <div></div>
          </div>
        )}
      </div>
    </div>
  );
}

// Editable bar row — drag body to move, drag edges to resize, inline-edit hours
function TimelineEditableRow({ entry, startISO, totalDays, DAY_W, FIXED, ganttWidth, onCommit, dayCells, todayISO }) {
  const e = entry;
  const offsetDays = e.start_date ? dateDiffDays(startISO, e.start_date) : 0;
  const lengthDays = Math.max(1, dateDiffDays(e.start_date, e.end_date) + 1);
  const baseLeft = offsetDays * DAY_W;
  const baseWidth = lengthDays * DAY_W - 4;

  // Transient drag state (visual offset/resize during drag)
  const [drag, setDrag] = useState(null); // { mode, snappedDx }
  const [hoursVal, setHoursVal] = useState(String(e.hours || 0));
  useEffect(() => { setHoursVal(String(e.hours || 0)); }, [e.hours]);

  const startDrag = (mode) => (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const startX = ev.clientX;
    const onMove = (mev) => {
      const dx = mev.clientX - startX;
      const snappedDx = Math.round(dx / DAY_W) * DAY_W;
      setDrag({ mode, snappedDx });
    };
    const onUp = (mev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      const dx = mev.clientX - startX;
      const days = Math.round(dx / DAY_W);
      setDrag(null);
      if (days === 0) return;
      let newStart = e.start_date;
      let newEnd = e.end_date;
      if (mode === "move") {
        newStart = addDays(e.start_date, days);
        newEnd = addDays(e.end_date, days);
      } else if (mode === "resize-l") {
        const candidate = addDays(e.start_date, days);
        // don't let start go past end
        newStart = candidate <= e.end_date ? candidate : e.end_date;
      } else if (mode === "resize-r") {
        const candidate = addDays(e.end_date, days);
        newEnd = candidate >= e.start_date ? candidate : e.start_date;
      }
      onCommit(e, { start_date: newStart, end_date: newEnd });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // Compute current bar position with transient drag preview
  let left = baseLeft;
  let width = baseWidth;
  if (drag) {
    if (drag.mode === "move") left = baseLeft + drag.snappedDx;
    else if (drag.mode === "resize-l") {
      const newLeft = baseLeft + drag.snappedDx;
      left = Math.min(newLeft, baseLeft + baseWidth - DAY_W);
      width = baseWidth + (baseLeft - left);
    } else if (drag.mode === "resize-r") {
      width = Math.max(DAY_W - 4, baseWidth + drag.snappedDx);
    }
  }

  // Compute display delivery date (end_date)
  const liveEnd = (() => {
    if (!drag) return e.end_date;
    const days = Math.round(drag.snappedDx / DAY_W);
    if (drag.mode === "move") return addDays(e.end_date, days);
    if (drag.mode === "resize-r") return addDays(e.end_date, days);
    return e.end_date;
  })();

  return (
    <div
      className="grid items-center border-b border-slate-100 hover:bg-slate-50/40"
      style={{ gridTemplateColumns: `${FIXED} ${ganttWidth}px` }}
      data-testid={`tl-row-${e.id}`}
    >
      <div className="px-3 h-12 flex items-center text-sm text-slate-800 truncate" title={e.person_email}>
        {e.person}
      </div>
      <div className="px-3 h-12 flex items-center text-sm text-slate-600 truncate" title={e.agent_label}>
        {e.agent_label}
      </div>
      <div className="px-3 h-12 flex items-center text-sm text-slate-600 truncate" title={e.model}>
        {e.model}
      </div>
      <div className="px-3 h-12 flex items-center">
        <input
          type="number"
          min="0"
          step="0.5"
          value={hoursVal}
          onChange={(ev) => setHoursVal(ev.target.value)}
          onBlur={() => {
            const v = parseFloat(hoursVal);
            const safe = Number.isFinite(v) && v >= 0 ? v : 0;
            if (safe !== (e.hours || 0)) onCommit(e, { hours: safe });
            else setHoursVal(String(e.hours || 0));
          }}
          onKeyDown={(ev) => { if (ev.key === "Enter") ev.target.blur(); }}
          className="w-14 h-7 px-1.5 rounded border border-slate-200 bg-white text-xs font-mono text-emerald-700 font-semibold text-center focus:outline-none focus:ring-2 focus:ring-blue-200"
          data-testid={`tl-hours-${e.id}`}
          aria-label="Hours"
        />
        <span className="ml-1 text-xs text-emerald-700 font-mono">h</span>
      </div>
      <div className="px-3 h-12 flex items-center text-xs text-slate-700 font-mono">
        {liveEnd || <span className="text-slate-400">—</span>}
      </div>
      <div className="relative h-12 border-l border-slate-200">
        {/* Day grid backdrop (weekend tint + today highlight) */}
        <div className="absolute inset-0 flex pointer-events-none">
          {dayCells.map((d, i) => {
            const dow = d.getDay();
            const isWeekend = dow === 0 || dow === 6;
            const iso = d.toISOString().slice(0, 10);
            const isToday = iso === todayISO;
            const isMon = dow === 1;
            return (
              <div
                key={i}
                className={`shrink-0 ${isMon ? "border-l-2 border-slate-200" : "border-l border-slate-100"} ${
                  isWeekend ? "bg-slate-50/60" : ""
                } ${isToday ? "bg-blue-50/40" : ""}`}
                style={{ width: DAY_W }}
              />
            );
          })}
        </div>
        {/* Bar */}
        <div
          className={`absolute top-1/2 -translate-y-1/2 h-7 rounded-md text-white text-[11px] flex items-center shadow-sm whitespace-nowrap select-none group ${
            drag ? "ring-2 ring-blue-300 cursor-grabbing" : "cursor-grab"
          } bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-500 hover:to-blue-700`}
          style={{ left, width }}
          onPointerDown={startDrag("move")}
          title={`${e.task || "Untitled"} · ${e.start_date} → ${liveEnd}`}
          data-testid={`tl-bar-${e.id}`}
        >
          {/* Left resize handle */}
          <div
            onPointerDown={startDrag("resize-l")}
            className="absolute left-0 top-0 h-full w-1.5 cursor-ew-resize rounded-l-md bg-white/0 hover:bg-white/30"
            data-testid={`tl-bar-resize-l-${e.id}`}
            title="Drag to change start date"
          />
          <span className="px-2 truncate">{e.task || "Untitled"}</span>
          {/* Right resize handle */}
          <div
            onPointerDown={startDrag("resize-r")}
            className="absolute right-0 top-0 h-full w-1.5 cursor-ew-resize rounded-r-md bg-white/0 hover:bg-white/30"
            data-testid={`tl-bar-resize-r-${e.id}`}
            title="Drag to change end date"
          />
        </div>
      </div>
    </div>
  );
}

export function TimelineDialog({ open, onOpenChange, agentTask, members, onSave }) {
  const [items, setItems] = useState([]);
  const [zoom, setZoom] = useState("day"); // day | week | month
  const trackRef = useRef(null);

  useEffect(() => {
    if (open && agentTask) setItems((agentTask.timeline || []).map((r) => ({ ...r })));
  }, [open, agentTask]);

  const todayISO = new Date().toISOString().slice(0, 10);
  const addRow = () => setItems((p) => [...p, {
    id: `tmp-${Date.now()}-${Math.random().toString(36).slice(-4)}`,
    user: "", task: "", start_date: todayISO, end_date: todayISO, hours: 0, depends_on: null,
  }]);
  const updateRow = (id, k, v) => setItems((p) => p.map((it) => (it.id === id ? { ...it, [k]: v } : it)));
  const removeRow = (id) => setItems((p) => p.filter((it) => it.id !== id));

  // Compute chart bounds
  const bounds = useMemo(() => {
    if (items.length === 0) return { start: todayISO, days: 30 };
    const starts = items.map((i) => i.start_date).filter(Boolean).map((d) => new Date(d).getTime());
    const ends = items.map((i) => i.end_date).filter(Boolean).map((d) => new Date(d).getTime());
    const s = starts.length ? new Date(Math.min(...starts)) : new Date(todayISO);
    const e = ends.length ? new Date(Math.max(...ends)) : new Date(todayISO);
    s.setDate(s.getDate() - 2);
    e.setDate(e.getDate() + 4);
    const days = Math.max(14, Math.round((e - s) / 86400000));
    return { start: s.toISOString().slice(0, 10), days };
  }, [items, todayISO]);

  const dayWidth = zoom === "day" ? 36 : zoom === "week" ? 12 : 4;
  const chartWidth = bounds.days * dayWidth;

  // Propagate dependency shifts on change (FS)
  const shiftDependants = (rows, changedId, deltaDays) => {
    if (!deltaDays) return rows;
    const result = [...rows];
    const visit = (id) => {
      for (const r of result) {
        if (r.depends_on === id) {
          r.start_date = addDays(r.start_date, deltaDays);
          r.end_date = addDays(r.end_date, deltaDays);
          visit(r.id);
        }
      }
    };
    visit(changedId);
    return result;
  };

  // Drag interactions on bars
  const startDragBar = (e, itemId, mode) => {
    e.preventDefault();
    const startX = e.clientX;
    const origItem = items.find((i) => i.id === itemId);
    if (!origItem) return;
    const origStart = origItem.start_date;
    const origEnd = origItem.end_date;
    const onMove = (ev) => {
      const dxPx = ev.clientX - startX;
      const dxDays = Math.round(dxPx / dayWidth);
      setItems((prev) => {
        const next = prev.map((it) => {
          if (it.id !== itemId) return { ...it };
          if (mode === "move") return { ...it, start_date: addDays(origStart, dxDays), end_date: addDays(origEnd, dxDays) };
          if (mode === "resize-right") {
            const newEnd = addDays(origEnd, dxDays);
            return { ...it, end_date: newEnd >= origStart ? newEnd : origStart };
          }
          if (mode === "resize-left") {
            const newStart = addDays(origStart, dxDays);
            return { ...it, start_date: newStart <= origEnd ? newStart : origEnd };
          }
          return it;
        });
        // propagate dependencies only when moving
        if (mode === "move") return shiftDependants(next, itemId, dxDays - (prev.find((x) => x.id === itemId)?.start_date === origStart ? 0 : 0));
        return next;
      });
    };
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const submit = async () => {
    try {
      const cleaned = items.map((r) => ({
        id: r.id, user: r.user || "", task: (r.task || "").trim(),
        start_date: r.start_date || "", end_date: r.end_date || "",
        hours: parseFloat(r.hours) || 0,
        depends_on: r.depends_on || null,
      })).filter((r) => r.user || r.task);
      await onSave({ timeline: cleaned });
      onOpenChange(false);
      toast.success("Timeline saved");
    } catch { toast.error("Save failed"); }
  };

  if (!agentTask) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl" data-testid="at-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Timeline</DialogTitle>
          <DialogDescription>Drag bars to shift dates. Resize from edges. Dependent bars shift automatically.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 text-xs font-medium">
            {["day", "week", "month"].map((z) => (
              <button key={z} onClick={() => setZoom(z)} className={`h-7 px-3 rounded-lg capitalize ${zoom === z ? "bg-white shadow-sm text-slate-900" : "text-slate-600"}`}>
                {z}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={addRow}><Plus className="h-3.5 w-3.5 mr-1" /> Add entry</Button>
        </div>
        <div className="rounded-xl border border-slate-200 overflow-auto" ref={trackRef}>
          <div className="grid" style={{ gridTemplateColumns: `200px 180px 80px 1fr`, minWidth: 600 + chartWidth }}>
            {/* Header */}
            <div className="bg-slate-100/70 border-b border-slate-200 px-3 h-10 flex items-center text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700">User</div>
            <div className="bg-slate-100/70 border-b border-slate-200 px-3 h-10 flex items-center text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700">Task</div>
            <div className="bg-slate-100/70 border-b border-slate-200 px-3 h-10 flex items-center text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700">Hrs</div>
            <div className="bg-slate-100/70 border-b border-slate-200 h-10 relative overflow-hidden" style={{ width: chartWidth }}>
              {Array.from({ length: bounds.days }).map((_, i) => {
                const d = addDays(bounds.start, i);
                const show = zoom === "day" || (zoom === "week" && i % 7 === 0) || (zoom === "month" && new Date(d).getDate() === 1);
                if (!show) return null;
                const dt = new Date(d + "T00:00:00");
                const dow = dt.getDay(); // 0=Sun..6=Sat
                const dowLetter = DOW_LETTERS[dow];
                return (
                  <div key={i} className={`absolute top-0 h-10 border-l ${dow === 1 ? "border-slate-300" : "border-slate-200"} flex flex-col items-center justify-center leading-none`} style={{ left: i * dayWidth, width: dayWidth }}>
                    {zoom === "day" ? (
                      <>
                        <span className={`text-[9px] font-semibold tracking-wider ${dow === 0 || dow === 6 ? "text-slate-400" : "text-slate-600"}`}>{dowLetter}</span>
                        <span className="text-[9px] text-slate-500 mt-0.5">{d.slice(5)}</span>
                      </>
                    ) : (
                      <span className="text-[10px] text-slate-500">{zoom === "week" ? d.slice(5) : d.slice(0, 7)}</span>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Rows */}
            {items.length === 0 ? (
              <div className="col-span-4 p-6 text-center text-xs text-slate-400">No entries yet — click "Add entry"</div>
            ) : items.map((it) => {
              const startOffset = it.start_date ? dateDiffDays(bounds.start, it.start_date) : 0;
              const dur = Math.max(1, dateDiffDays(it.start_date, it.end_date) + 1);
              return (
                <React.Fragment key={it.id}>
                  <div className="px-2 h-12 flex items-center border-b border-slate-100">
                    <Select value={it.user || "__none__"} onValueChange={(v) => updateRow(it.id, "user", v === "__none__" ? "" : v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`tl-user-${it.id}`}><SelectValue placeholder="User" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__"><span className="text-slate-500">Unassigned</span></SelectItem>
                        {members.map((m) => <SelectItem key={m.id} value={m.email}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="px-2 h-12 flex items-center border-b border-slate-100">
                    <Input value={it.task} onChange={(e) => updateRow(it.id, "task", e.target.value)} placeholder="Task" className="h-8 text-sm" data-testid={`tl-task-${it.id}`} />
                  </div>
                  <div className="px-2 h-12 flex items-center border-b border-slate-100">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      value={it.hours ?? 0}
                      onChange={(e) => updateRow(it.id, "hours", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                      placeholder="0"
                      className="h-8 text-sm"
                      data-testid={`tl-hours-${it.id}`}
                    />
                  </div>
                  <div className="relative h-12 border-b border-slate-100" style={{ width: chartWidth }}>
                    {Array.from({ length: bounds.days + 1 }).map((_, i) => (
                      <div key={i} className="absolute top-0 bottom-0 border-l border-slate-100" style={{ left: i * dayWidth }} />
                    ))}
                    {it.start_date && it.end_date && (
                      <div
                        className="absolute top-2 h-8 rounded-md bg-gradient-to-r from-blue-500 to-indigo-500 shadow-sm text-white text-[11px] font-medium flex items-center px-2 cursor-grab active:cursor-grabbing select-none"
                        style={{ left: startOffset * dayWidth, width: dur * dayWidth }}
                        onMouseDown={(e) => startDragBar(e, it.id, "move")}
                        data-testid={`tl-bar-${it.id}`}
                        title={`${it.start_date} → ${it.end_date}`}
                      >
                        <div
                          className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/20"
                          onMouseDown={(e) => { e.stopPropagation(); startDragBar(e, it.id, "resize-left"); }}
                        />
                        <span className="truncate flex-1">{it.task || "—"}</span>
                        <div
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/20"
                          onMouseDown={(e) => { e.stopPropagation(); startDragBar(e, it.id, "resize-right"); }}
                        />
                      </div>
                    )}
                    <button
                      onClick={() => removeRow(it.id)}
                      className="absolute -right-2 top-3 h-6 w-6 rounded-md bg-white border border-slate-200 text-slate-400 hover:text-rose-600 hover:border-rose-300 flex items-center justify-center opacity-0 hover:opacity-100 group-hover:opacity-100"
                      title="Remove entry"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
        {/* Dependencies editor */}
        {items.length > 0 && (
          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-2">Dependencies (finish-to-start)</p>
            <div className="space-y-2 max-h-36 overflow-y-auto">
              {items.map((it) => (
                <div key={it.id} className="flex items-center gap-2 text-sm">
                  <span className="truncate text-slate-700 min-w-32">{it.task || "(no task)"}</span>
                  <span className="text-xs text-slate-400">depends on</span>
                  <Select value={it.depends_on || "__none__"} onValueChange={(v) => updateRow(it.id, "depends_on", v === "__none__" ? null : v)}>
                    <SelectTrigger className="h-7 text-xs w-52"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {items.filter((x) => x.id !== it.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.task || x.id.slice(0, 6)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="btn-brand">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Main table component ---
// Draggable agent-name tab (horizontal). Click selects; drag (>5px) reorders.
function SortableAgentTab({ id, label, count, active, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <button
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      data-testid={`at-agent-tab-${id}`}
      className={`h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors cursor-grab active:cursor-grabbing ${
        active ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
      }`}
    >
      {label}
      <span className={`text-[10px] px-1.5 rounded-full ${active ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>
    </button>
  );
}

export default function AgentTaskTable({ agents, apis, members, onAgentUpdate }) {
  const [rows, setRows] = useState([]);
  const [productRows, setProductRows] = useState([]);
  const [refsOpen, setRefsOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [remarksOpen, setRemarksOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [personFilter, setPersonFilter] = useState("__all__"); // member email or "__all__" or "__unassigned__"
  const [agentNameFilter, setAgentNameFilter] = useState("__all__"); // agent_name or "__all__"
  const [viewMode, setViewMode] = useState("list"); // list | timeline
  const [tabOrder, setTabOrder] = useState([]); // persisted agent-name tab order (shared with Cost)

  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const tabSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRows((prev) => {
      const oldIdx = prev.findIndex((r) => r.id === active.id);
      const newIdx = prev.findIndex((r) => r.id === over.id);
      if (oldIdx === -1 || newIdx === -1) return prev;
      const next = arrayMove(prev, oldIdx, newIdx);
      api.post("/agent-tasks/reorder", { order: next.map((r) => r.id) }).catch(() => toast.error("Reorder failed"));
      return next;
    });
  };

  const load = async () => {
    try {
      const { data } = await api.get("/agent-tasks");
      setRows(data);
    } catch (e) { console.error(e); }
  };
  const loadProductRows = async () => {
    try {
      const { data } = await api.get("/product-tasks");
      setProductRows(data);
    } catch (e) { console.error(e); }
  };
  const loadTabOrder = async () => {
    try {
      const { data } = await api.get("/settings/agent-tabs");
      setTabOrder(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); loadProductRows(); loadTabOrder(); }, []);

  // Hydrate agent rows so we can display cascaded data
  const agentById = useMemo(() => {
    const m = new Map();
    for (const a of agents) m.set(a.id, a);
    return m;
  }, [agents]);
  const apiById = useMemo(() => {
    const m = new Map();
    for (const a of apis) m.set(a.id, a);
    return m;
  }, [apis]);

  // Cascading option lists
  const agentNames = useMemo(() => Array.from(new Set(agents.map((a) => a.agent_name).filter(Boolean))).sort(), [agents]);
  const subAgentsFor = (name) => Array.from(new Set(agents.filter((a) => a.agent_name === name).map((a) => a.sub_agent))).filter((v) => v !== undefined).sort();
  const subSubFor = (name, sub) => Array.from(new Set(agents.filter((a) => a.agent_name === name && a.sub_agent === sub).map((a) => a.sub_sub_agent))).filter((v) => v !== undefined).sort();
  const modelsFor = (name, sub, subsub) => agents.filter((a) => a.agent_name === name && a.sub_agent === sub && a.sub_sub_agent === subsub);

  const getAgent = (row) => agentById.get(row.agent_id);

  // Rows currently visible given the active agent-name tab + person filter.
  const filteredRows = useMemo(() => rows.filter((r) => {
    if (agentNameFilter !== "__all__") {
      const ag = r.agent_id ? agentById.get(r.agent_id) : null;
      const name = (r.agent_name || ag?.agent_name || "").trim() || "—";
      if (name !== agentNameFilter) return false;
    }
    if (personFilter === "__all__") return true;
    if (personFilter === "__unassigned__") {
      return !(r.workflow_integrator || "") && !(r.tester_adopter || "");
    }
    return r.workflow_integrator === personFilter || r.tester_adopter === personFilter;
  }), [rows, agentNameFilter, personFilter, agentById]);

  const memberName = (email) => members.find((m) => m.email === email)?.name || email || "";

  // Ordered agent-name tabs (persisted order via /settings/agent-tabs; new names
  // fall to the end alphabetically; uncategorized "—" defaults last but is movable).
  const agentTabs = useMemo(() => {
    const counts = new Map();
    for (const r of rows) {
      const ag = r.agent_id ? agentById.get(r.agent_id) : null;
      const name = (r.agent_name || ag?.agent_name || "").trim() || "—";
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    const nameSet = new Set([...counts.keys(), ...agentNames]);
    const names = Array.from(nameSet);
    const orderIndex = new Map(tabOrder.map((n, i) => [n, i]));
    names.sort((a, b) => {
      const ia = orderIndex.has(a) ? orderIndex.get(a) : Number.POSITIVE_INFINITY;
      const ib = orderIndex.has(b) ? orderIndex.get(b) : Number.POSITIVE_INFINITY;
      if (ia !== ib) return ia - ib;
      if (a === "—") return 1;
      if (b === "—") return -1;
      return a.localeCompare(b);
    });
    return { names, counts };
  }, [rows, agentNames, agentById, tabOrder]);

  const handleTabDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const names = agentTabs.names;
    const oldIdx = names.indexOf(active.id);
    const newIdx = names.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const next = arrayMove(names, oldIdx, newIdx);
    setTabOrder(next);
    try { await api.put("/settings/agent-tabs", { order: next }); }
    catch { toast.error("Failed to save tab order"); }
  };

  const handleCopyTable = async () => {
    if (filteredRows.length === 0) { toast.error("Nothing to copy yet"); return; }
    const headers = ["Agent", "Sub-agent", "Sub-sub-agent", "Model", "Agent Status", "Workflow", "Tester", "Date", "Remarks", "Status"];
    const data = filteredRows.map((r) => {
      const ag = r.agent_id ? agentById.get(r.agent_id) : null;
      const p = rowPrimaryEntry(r);
      const sLbl = fmtShortDate(p?.start_date);
      const eLbl = fmtShortDate(p?.end_date);
      const dateLabel = (!sLbl && !eLbl) ? "" : (eLbl && eLbl !== sLbl ? `${sLbl} – ${eLbl}` : sLbl || eLbl);
      return [
        (r.agent_name || ag?.agent_name || "").trim(),
        (r.sub_agent ?? ag?.sub_agent) || "",
        (r.sub_sub_agent ?? ag?.sub_sub_agent) || "",
        ag?.model_name || "",
        ag?.status || "",
        memberName(r.workflow_integrator),
        memberName(r.tester_adopter),
        dateLabel,
        p?.task || "",
        r.status || "Assigned",
      ];
    });
    const ok = await copyTableToClipboard({ headers, rows: data });
    if (ok) toast.success(`Copied ${data.length} ${data.length === 1 ? "row" : "rows"} — paste into Docs, email or Sheets`);
    else toast.error("Copy failed — your browser blocked clipboard access");
  };

  const pickAgent = (rowId, partial) => {
    // `partial` is { sub_agent, sub_sub_agent, model_id? }. We must KEEP the row's
    // agent group (agent_name) even when agent_id is cleared, otherwise the row would
    // drop out of its current tab.
    setRows((prev) => prev.map((r) => {
      if (r.id !== rowId) return r;
      const ag = agentById.get(r.agent_id);
      const keepName = r.agent_name || ag?.agent_name || (agentNameFilter !== "__all__" ? agentNameFilter : "");
      return { ...r, agent_name: keepName, ...partial };
    }));
  };

  const handleCreate = async () => {
    try {
      const body = agentNameFilter !== "__all__" ? { agent_name: agentNameFilter } : {};
      const { data } = await api.post("/agent-tasks", body);
      setRows((prev) => [...prev, data]);
    } catch { toast.error("Failed to add row"); }
  };
  const handleUpdate = async (id, patch) => {
    try {
      const { data } = await api.patch(`/agent-tasks/${id}`, patch);
      setRows((prev) => prev.map((r) => (r.id === id ? data : r)));
      return data;
    } catch { toast.error("Update failed"); throw new Error("update failed"); }
  };
  const handleDelete = async (id) => {
    try { await api.delete(`/agent-tasks/${id}`); setRows((prev) => prev.filter((r) => r.id !== id)); }
    catch { toast.error("Delete failed"); }
  };
  const handleDuplicate = async (r) => {
    try { await api.post(`/agent-tasks/${r.id}/duplicate`); await load(); }
    catch { toast.error("Duplicate failed"); }
  };

  // When user picks the full quadruple, resolve to exact agent
  const ensureAgentId = async (rowId, q) => {
    const row = rows.find((r) => r.id === rowId);
    const ag = agentById.get(row?.agent_id);
    // Persist agent_name alongside agent_id so the row keeps its tab membership.
    const keepName = q.agent_name || row?.agent_name || ag?.agent_name || (agentNameFilter !== "__all__" ? agentNameFilter : "");
    const matches = modelsFor(q.agent_name, q.sub_agent, q.sub_sub_agent);
    if (q.model_id) {
      await handleUpdate(rowId, { agent_id: q.model_id, agent_name: keepName });
      return;
    }
    if (matches.length === 1) {
      await handleUpdate(rowId, { agent_id: matches[0].id, agent_name: keepName });
    }
  };

  return (
    <div className="space-y-4" data-testid="agent-task-table">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-semibold">Agent Workflow</h3>
          <p className="text-xs text-slate-500">Rows link to entries from the Agents module. Remarks and References sync both ways.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-xl bg-white border border-slate-200 p-1" data-testid="at-view-toggle">
            <button
              onClick={() => setViewMode("list")}
              className={`h-8 px-3 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${viewMode === "list" ? "btn-brand" : "text-slate-600 hover:bg-slate-50"}`}
              data-testid="at-view-list"
            >
              <Rows3 className="h-3.5 w-3.5" /> List
            </button>
            <button
              onClick={() => setViewMode("timeline")}
              className={`h-8 px-3 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${viewMode === "timeline" ? "btn-brand" : "text-slate-600 hover:bg-slate-50"}`}
              data-testid="at-view-timeline"
            >
              <GanttChartSquare className="h-3.5 w-3.5" /> Timeline
            </button>
          </div>
          <Select value={personFilter} onValueChange={setPersonFilter}>
            <SelectTrigger className="h-10 w-56 rounded-xl text-sm" data-testid="at-person-filter">
              <SelectValue placeholder="Filter by person" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__"><span className="text-slate-700">All people</span></SelectItem>
              <SelectItem value="__unassigned__"><span className="text-slate-500">Unassigned only</span></SelectItem>
              {members.map((m) => (
                <SelectItem key={m.id} value={m.email}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {viewMode === "list" && (
            <>
              <Button
                onClick={handleCopyTable}
                variant="outline"
                className="h-10 rounded-xl border-slate-200 text-slate-700 hover:bg-slate-50"
                data-testid="at-copy-table"
                title="Copy the whole list as a table (paste into Docs, email or Sheets)"
              >
                <ClipboardCopy className="h-4 w-4 mr-1" /> Copy
              </Button>
              <Button onClick={handleCreate} className="btn-brand h-10 rounded-xl" data-testid="at-new-row">
                <Plus className="h-4 w-4 mr-1" /> Add row
              </Button>
            </>
          )}
        </div>
      </div>

      {personFilter !== "__all__" && (
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <span>Showing only rows where</span>
          <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 font-medium">
            {personFilter === "__unassigned__"
              ? "Workflow or Tester is unassigned"
              : `${members.find((m) => m.email === personFilter)?.name || personFilter} is the Workflow or Tester`}
          </span>
          <button onClick={() => setPersonFilter("__all__")} className="text-blue-600 hover:underline" data-testid="at-person-filter-clear">Clear</button>
        </div>
      )}

      {/* Agent-name category tabs (draggable to reorder; order persists) */}
      {viewMode === "list" && agentTabs.names.length > 0 && (
        <>
          <div className="flex items-center gap-1 flex-wrap" data-testid="at-agent-tabs">
            <button
              onClick={() => setAgentNameFilter("__all__")}
              className={`h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors ${
                agentNameFilter === "__all__"
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="at-agent-tab-all"
            >
              All
              <span className={`text-[10px] px-1.5 rounded-full ${agentNameFilter === "__all__" ? "bg-white/20" : "bg-slate-100"}`}>
                {rows.length}
              </span>
            </button>
            <DndContext sensors={tabSensors} collisionDetection={closestCenter} onDragEnd={handleTabDragEnd}>
              <SortableContext items={agentTabs.names} strategy={horizontalListSortingStrategy}>
                {agentTabs.names.map((n) => (
                  <SortableAgentTab
                    key={n}
                    id={n}
                    label={n}
                    count={agentTabs.counts.get(n) || 0}
                    active={agentNameFilter === n}
                    onClick={() => setAgentNameFilter(n)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
          <AgentOwnersBar agentName={agentNameFilter} members={members} />
        </>
      )}

      {viewMode === "timeline" ? (
        <AgentTimelineView
          rows={rows}
          productRows={productRows}
          agents={agents}
          members={members}
          personFilter={personFilter}
          onCommit={async (entry, patch) => {
            const nextTimeline = (entry.row_timeline || []).map((t) =>
              t.id === entry.id ? { ...t, ...patch } : t
            );
            if (entry.source === "product") {
              setProductRows((prev) => prev.map((r) => (r.id === entry.task_id ? { ...r, timeline: nextTimeline } : r)));
              try {
                const { data } = await api.patch(`/product-tasks/${entry.task_id}`, { timeline: nextTimeline });
                setProductRows((prev) => prev.map((r) => (r.id === entry.task_id ? data : r)));
              } catch { toast.error("Update failed"); loadProductRows(); }
              return;
            }
            // Default: agent
            setRows((prev) => prev.map((r) => (r.id === entry.task_id ? { ...r, timeline: nextTimeline } : r)));
            try {
              const updated = await handleUpdate(entry.task_id, { timeline: nextTimeline });
              if (updated) {
                setRows((prev) => prev.map((r) => (r.id === entry.task_id ? updated : r)));
              }
            } catch {
              load();
            }
          }}
        />
      ) : (
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <div style={{ minWidth: 1500 }}>
          <div className="sticky top-0 z-30 grid gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 px-4 h-11 items-center bg-slate-100/95 backdrop-blur-sm border-b border-slate-200 shadow-[0_1px_0_rgba(0,0,0,0.03)] whitespace-nowrap" style={{ gridTemplateColumns: GRID }}>
            <div></div>
            <div>Sub-agent</div>
            <div>Sub-sub-agent</div>
            <div>Model</div>
            <div>Agent Status</div>
            <div>Workflow</div>
            <div>Tester</div>
            <div>Date</div>
            <div>Remarks</div>
            <div>Status</div>
            <div className="text-center">Actions</div>
          </div>
          {(() => {
            return filteredRows.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">
                {rows.length === 0 ? 'No rows yet — click "Add row"' : "No rows match the current filter."}
              </div>
            ) : (
              <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={filteredRows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                  {filteredRows.map((r) => {
                  const ag = getAgent(r);
                  const sMeta = STATUS_META[ag?.status] || STATUS_META["Not Started"];
                  const subs = subAgentsFor(r.agent_name || ag?.agent_name || "");
                  const subsubs = subSubFor(r.agent_name || ag?.agent_name || "", r.sub_agent || ag?.sub_agent || "");
                  const models = modelsFor(r.agent_name || ag?.agent_name || "", r.sub_agent || ag?.sub_agent || "", r.sub_sub_agent || ag?.sub_sub_agent || "");
                  const refsCount = (ag?.references || []).length || ((ag?.reference_link || "").trim() ? 1 : 0);
                  return (
                    <AgentTaskRow
                      key={r.id}
                      r={r}
                      ag={ag}
                      agentNames={agentNames}
                      subs={subs}
                      subsubs={subsubs}
                      models={models}
                      members={members}
                      sMeta={sMeta}
                      refsCount={refsCount}
                      pickAgent={pickAgent}
                      ensureAgentId={ensureAgentId}
                      handleUpdate={handleUpdate}
                      handleDuplicate={handleDuplicate}
                      handleDelete={handleDelete}
                      onOpenRefs={(row) => { setActiveRow(row); setRefsOpen(true); }}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
            );
          })()}
        </div>
      </div>
      )}

      <ReferencesDialog
        open={refsOpen}
        onOpenChange={setRefsOpen}
        agent={activeRow ? getAgent(activeRow) : null}
        onSave={async (patch) => {
          const ag = getAgent(activeRow);
          if (ag) await onAgentUpdate(ag.id, patch);
        }}
      />
      <RemarksDialog
        open={remarksOpen}
        onOpenChange={setRemarksOpen}
        agent={activeRow ? getAgent(activeRow) : null}
        onSave={async (patch) => {
          const ag = getAgent(activeRow);
          if (ag) await onAgentUpdate(ag.id, patch);
        }}
      />
      <TimelineDialog
        open={timelineOpen}
        onOpenChange={setTimelineOpen}
        agentTask={activeRow}
        members={members}
        onSave={async (patch) => {
          if (!activeRow) return;
          await handleUpdate(activeRow.id, patch);
        }}
      />
    </div>
  );
}
